import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import Logger from '../../services/Logger';
import ReactSelect from '../../UIComponents/ReactSelect';
import {
  changeOrder,
} from '../../store/products/actions';
import {
  buildSelectStyles,
  resolvePlaceholderColor,
  getDefaultSelectStyleTokens,
} from './helpers';
import { showDialog } from '../dialog';
import l18n from '../../shared/strings';
import * as utils from '../../shared/utils';
import Tooltip from '../Tooltip';

const ImageOrder = (props) => {
  const dispatch = useDispatch();
  const {
    order,
    productAssetsLength,
    assetId,
    disabled,
    isImage,
  } = props;

  const orderOptions = useMemo(() => Array.from({ length: productAssetsLength }, (_, index) => ({
    label: index === 0 ? '1 (Main image)' : `${index + 1}`,
    value: index,
    isDisabled: !isImage && index === 0,
  })), [isImage, productAssetsLength]);

  const selectedOption = useMemo(() => {
    const option = orderOptions.find((o) => o.value === order?.index) || null;
    if (option && option.value === 0) return { ...option, label: '1' };
    return option;
  }, [orderOptions, order]);

  const placeholderColor = useMemo(() => resolvePlaceholderColor('#999'), []);
  const styleTokens = useMemo(() => getDefaultSelectStyleTokens(placeholderColor), [placeholderColor]);
  const styles = useMemo(() => buildSelectStyles(styleTokens), [styleTokens]);

  const confirm = useCallback((newOrder) => {
    Logger.log('User', 'ProductsReorderMediaModalChangesConfirmed', {
      productId: order.productId,
      positionN: newOrder.value,
      assetId,
    });
    dispatch(changeOrder({
      assetId,
      order: newOrder.value,
      productId: order.productId,
    }));
  }, [dispatch, assetId, order.productId]);

  const handleChange = useCallback((newOrder) => {
    const notShowDialogReorderMedia = utils.LocalStorage.get('picsio.notShowDialogReorderMedia') || false;
    if (notShowDialogReorderMedia) {
      confirm(newOrder);
      return;
    }
    Logger.log('User', 'ProductsReorderMediaPositionSelected', {
      assetId,
      positionN: newOrder.value,
      productId: order.productId,
    });

    showDialog({
      title: l18n.DIALOGS.CHANGE_IMAGE_ORDER.TITLE,
      text: l18n.DIALOGS.CHANGE_IMAGE_ORDER.TEXT(),
      textBtnOk: l18n.DIALOGS.CHANGE_IMAGE_ORDER.textBtnOk,
      textBtnCancel: l18n.DIALOGS.CHANGE_IMAGE_ORDER.textBtnCancel,
      onOk: ({ checkbox }) => {
        if (checkbox) {
          utils.LocalStorage.set('picsio.notShowDialogReorderMedia', checkbox);
        }
        confirm(newOrder);
      },
      onCancel: () => {
        Logger.log('User', 'ProductsReorderMediaModalClosed');
      },
      checkbox: {
        value: false,
        label: l18n.DIALOGS.CHANGE_IMAGE_ORDER.checkboxLabel,
      },
    });
  }, [assetId, confirm, order.productId]);

  const handleClick = useCallback(() => {
    Logger.log('User', 'ProductsReorderMediaDropdownOpened', {
      assetId,
      productId: order.productId,
    });
  }, [assetId, order.productId]);

  const customFilterOption = useCallback((option, inputValue) => option.label.toLowerCase().startsWith(inputValue.toLowerCase()), []);

  return (
    <Tooltip
      placement="top"
      content={l18n.mainImageTooltip}
    >
      <div className={cn('imageOrder', { disabled })}>
        <ReactSelect
          onClick={handleClick}
          options={orderOptions}
          value={selectedOption}
          onChange={handleChange}
          menuPortalTarget={document.body}
          styles={styles}
          isDisabled={disabled}
          filterOption={customFilterOption}
        />
      </div>
    </Tooltip>
  );
};

ImageOrder.propTypes = {
  order: PropTypes.number,
  productAssetsLength: PropTypes.number.isRequired,
  assetId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  isImage: PropTypes.bool,
};

ImageOrder.defaultProps = {
  order: null,
  disabled: false,
  isImage: true,
};

export default ImageOrder;
